import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Copy, Globe, Calendar, User, Building } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface WhoisData {
  domain: string;
  registrar: string;
  registrationDate: string;
  expirationDate: string;
  nameServers: string[];
  status: string[];
  registrant: {
    name: string;
    organization: string;
    country: string;
  };
}

export const WhoisLookupTool: React.FC = () => {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [whoisData, setWhoisData] = useState<WhoisData | null>(null);
  const [error, setError] = useState('');

  // Mock whois data for demonstration
  const mockWhoisLookup = async (domainName: string): Promise<WhoisData> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      domain: domainName,
      registrar: 'Example Registrar Inc.',
      registrationDate: '2020-01-15',
      expirationDate: '2025-01-15',
      nameServers: [
        'ns1.example.com',
        'ns2.example.com',
        'ns3.example.com'
      ],
      status: [
        'clientTransferProhibited',
        'clientUpdateProhibited'
      ],
      registrant: {
        name: 'John Doe',
        organization: 'Example Organization',
        country: 'United States'
      }
    };
  };

  const handleLookup = async () => {
    if (!domain.trim()) {
      setError(t('tools.whoisLookup.enterDomainError'));
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domain.trim())) {
      setError(t('tools.whoisLookup.invalidDomainError'));
      return;
    }

    setLoading(true);
    setError('');
    setWhoisData(null);

    try {
      // In a real implementation, you would call an actual WHOIS API
      const data = await mockWhoisLookup(domain.trim().toLowerCase());
      setWhoisData(data);
    } catch (err) {
      setError(t('tools.whoisLookup.lookupError'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const formatWhoisData = (data: WhoisData) => {
    return `${t('tools.whoisLookup.domain')}: ${data.domain}
${t('tools.whoisLookup.registrar')}: ${data.registrar}
${t('tools.whoisLookup.registrationDate')}: ${data.registrationDate}
${t('tools.whoisLookup.expirationDate')}: ${data.expirationDate}
${t('tools.whoisLookup.status')}: ${data.status.join(', ')}
${t('tools.whoisLookup.nameServers')}: ${data.nameServers.join(', ')}
${t('tools.whoisLookup.registrant')}: ${data.registrant.name}
${t('tools.whoisLookup.organization')}: ${data.registrant.organization}
${t('tools.whoisLookup.country')}: ${data.registrant.country}`;
  };

  const sampleDomains = [
    'google.com',
    'github.com',
    'stackoverflow.com',
    'mozilla.org',
    'wikipedia.org'
  ];

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="w-5 h-5" />
            <span>{t('tools.whoisLookup.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder={t('tools.whoisLookup.enterDomain')}
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
              />
              <Button
                onClick={handleLookup}
                disabled={loading}
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? t('tools.whoisLookup.lookingUp') : t('tools.whoisLookup.lookup')}
              </Button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.whoisLookup.sampleDomains')}:
              </label>
              <div className="flex flex-wrap gap-2">
                {sampleDomains.map((sampleDomain) => (
                  <Button
                    key={sampleDomain}
                    onClick={() => setDomain(sampleDomain)}
                    variant="outline"
                    size="sm"
                  >
                    {sampleDomain}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {whoisData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.whoisLookup.whoisInformationFor')} {whoisData.domain}</span>
              <Button
                onClick={() => copyToClipboard(formatWhoisData(whoisData))}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3 mr-1" />
                {t('tools.whoisLookup.copyAll')}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.registrar')}:</span>
                    <span>{whoisData.registrar}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.registration')}:</span>
                    <span>{whoisData.registrationDate}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.expiration')}:</span>
                    <span>{whoisData.expirationDate}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.registrant')}:</span>
                    <span>{whoisData.registrant.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.organization')}:</span>
                    <span>{whoisData.registrant.organization}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{t('tools.whoisLookup.country')}:</span>
                    <span>{whoisData.registrant.country}</span>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.whoisLookup.domainStatus')}</h4>
                <div className="flex flex-wrap gap-2">
                  {whoisData.status.map((status, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    >
                      {status}
                    </span>
                  ))}
                </div>
              </div>

              {/* Name Servers */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.whoisLookup.nameServers')}</h4>
                <div className="space-y-1">
                  {whoisData.nameServers.map((ns, index) => (
                    <div key={index} className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      {ns}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.whoisLookup.aboutWhoisLookup')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.whoisLookup.whatIsWhois')}</h4>
              <p>
                {t('tools.whoisLookup.whoisDescription')}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.whoisLookup.informationAvailable')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.whoisLookup.info1')}</li>
                <li>{t('tools.whoisLookup.info2')}</li>
                <li>{t('tools.whoisLookup.info3')}</li>
                <li>{t('tools.whoisLookup.info4')}</li>
                <li>{t('tools.whoisLookup.info5')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.whoisLookup.privacyNote')}</h4>
              <p>
                {t('tools.whoisLookup.privacyDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
